// Design tokens — single source of truth for colors, spacing, radius,
// typography. UX_REVIEW.md flagged drift (gold accent used as
// #C9A227 / #b45309 / #d97706 / #fbbf24 / #e6a83a / #c8a54e).
//
// New views SHOULD import from here. Existing views can migrate
// incrementally — the hex values baked into inline styles today are
// not blocking and the visual drift is acceptable during the
// migration window.
//
// Rule for adding tokens: only add when the value appears in 3+
// places with the same intent. One-off values stay inline.

export const color = {
  // Neutrals — chosen with a slight warm bias (not the AI-generic
  // pure grey). See docs/audit/UX_REVIEW.md for the reasoning.
  ink:      '#0f172a',   // primary text on light bg
  ink2:     '#1a1815',   // memo/serif body (Georgia)
  muted:    '#64748b',   // secondary text, timestamps
  muted2:   '#94a3b8',   // tertiary text, hints
  rule:     '#e2e8f0',   // hairline borders on light bg
  ground:   '#ffffff',   // card fill on light bg
  ground2:  '#f8fafc',   // page fill on light bg
  memo:     '#efe8d7',   // Daily Memo cream (Bezos memo ground)

  // Accents
  amber:      '#b45309', // canonical brand amber (Referral CC eyebrow, section labels)
  amberLite:  '#fef3c7', // amber-error card fill
  amberBd:    '#fcd34d', // amber-error card border
  amberInk:   '#92400e', // amber-error card text

  // Semantic — signal, not decoration
  positive:   '#059669', // "up" trend, saved successfully
  positive2:  '#dcfce7', // positive card fill
  negative:   '#dc2626', // "down" trend, at-risk
  negative2:  '#fef2f2', // negative card fill

  // Founder-memo card scheme
  memoInk:      '#1a1815',
  memoAccent:   '#8c1e14', // venetian red — used for the memo's single accent
  memoGold:     '#c8a54e', // gilt pull, memo receipts
  memoRule:     '#c9c1af',
  memoGround2:  '#f5eecb',

  // Founder-memo dark card (Single Decision)
  darkGround:   '#0f172a',
  darkText:     '#e2e8f0',
  darkRule:     '#334155',
  darkAmber:    '#fbbf24', // eyebrow on dark card
  crossSell:    '#0891b2', // cyan border on cross-sell single decision
  crossSellText:'#7dd3fc',
};

export const radius = {
  compact: 4,
  medium:  8,
  large:   12,
  card:    10, // KPI tiles, error cards
  pill:    999,
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 28,
};

export const font = {
  display: '"Times New Roman", "Times", "Playfair Display", serif',
  body:    'Georgia, "Iowan Old Style", "Palatino Linotype", serif',
  ui:      '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono:    '"JetBrains Mono", "SF Mono", ui-monospace, "Cascadia Code", monospace',
};

export const size = {
  eyebrow:   10,
  body:      13,
  bodyLarge: 15,
  title:     20,
  titleXl:   24,
  displayLg: 32,
  bigNumber: 26, // Four Numbers header, Single Decision facts
};

// ═══ Cockpit design system ══════════════════════════════════════════
//
// Added 2026-07-12 per the master aesthetic prompt. Dark navy sidebar +
// bright light workspace. Use these for any new Cockpit / Health surface;
// the founder-memo tokens above stay for existing memo-styled panels.
//
// Rule of thumb: `cockpit.*` for the new UI, everything above for legacy.
export const cockpit = {
  color: {
    // Sidebar — obsidian HQ. Retuned 2026-07-16 for the AI Headquarters
    // direction: warm-dark with amber hairlines. Aligned with
    // src/hq/theme.js. Was deep-navy pre-HQ retune.
    sidebarBg:      '#050709',
    sidebarBg2:     '#090C10',
    sidebarBorder:  'rgba(245,166,35,0.18)',
    sidebarText:    '#F4F4F2',
    sidebarMuted:   '#7C838C',
    sidebarActive:  'rgba(245,166,35,0.12)',
    sidebarActiveBorder: '#F5A623',

    // Workspace — warm-dark, smoked-glass panels.
    pageBg:         '#050709',
    surface:        'rgba(14,18,23,0.86)',
    surface2:       '#090C10',
    surfaceHover:   'rgba(245,166,35,0.06)',
    border:         'rgba(245,166,35,0.18)',
    borderStrong:   'rgba(245,166,35,0.30)',
    textPrimary:    '#F4F4F2',
    textSecondary:  '#B9BEC6',
    textMuted:      '#7C838C',

    // AI — amber. The intelligence signal. Was purple pre-HQ retune.
    ai:             '#F5A623',
    aiHover:        '#FFC457',
    aiSoft:         'rgba(245,166,35,0.10)',
    aiBorder:       'rgba(245,166,35,0.42)',

    // Health — restrained green for dark surfaces.
    health:         '#5BA36F',
    healthHover:    '#7BC090',
    healthSoft:     'rgba(91,163,111,0.12)',
    healthBorder:   'rgba(91,163,111,0.42)',

    // Revenue — gold amber for money.
    revenue:        '#E4B44E',
    revenueSoft:    'rgba(228,180,78,0.14)',
    revenueBorder:  'rgba(228,180,78,0.42)',

    // Semantic — retuned for dark surfaces.
    success:        '#5BA36F',
    successSoft:    'rgba(91,163,111,0.14)',
    warning:        '#F5A623',
    warningSoft:    'rgba(245,166,35,0.12)',
    danger:         '#C4584A',
    dangerSoft:     'rgba(196,88,74,0.14)',
  },
  radius: {
    sm: 7,
    md: 10,
    lg: 14,
    xl: 18,
  },
  shadow: {
    card:    '0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(15,23,42,0.035)',
    overlay: '0 20px 60px rgba(15,23,42,0.16)',
  },
  spacing: {
    pagePad:   32,
    cardGap:   16,
    sectionGap: 24,
  },
  layout: {
    sidebarWidth:  232,
    topBarHeight:  68,
    maxContent:    1680,
  },
  type: {
    display: '"Geist", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body:    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono:    '"JetBrains Mono", ui-monospace, monospace',
  },
};

// Amber error box — used in ~14 panels this session. Extract to a
// convenience style block since it's the most-repeated pattern.
export const amberErrorBox = {
  background: color.amberLite,
  border:     `1px solid ${color.amberBd}`,
  borderRadius: radius.card,
  padding:    '10px 12px',
  fontSize:   12,
  color:      color.amberInk,
  marginBottom: 8,
};

// ═══ Six Figure Agency OS · Phase 2 tokens ═══════════════════════════
//
// Added 2026-07-15 per the Master Directive. Extends `cockpit` tokens
// with: ARIA state semantics, motion language, text hierarchy, surface
// elevation levels, and signal-vs-accent separation.
//
// Rule: NEW Command-Center-scale surfaces (Command Center, Leads
// Intelligence, Pre-Call, Live Call, Revenue Intelligence, Client
// Intelligence) use `sixfigure.*`. Legacy panels + memo-style
// surfaces keep the tokens above.
//
// See docs/six-figure-redesign/README.md for the phased plan.

export const sixfigure = {
  // ── SURFACE ELEVATION LEVELS ────────────────────────────────────
  // Not just "cards on a background". The vision requires distinct
  // levels: environment (deepest), workspace (main plane), elevated
  // (raised card), intelligence (ARIA insight surface), command
  // (primary action rail), modal, critical.
  surface: {
    environment:  '#f4f7fb',                 // page ambient — same as cockpit.pageBg
    workspace:    '#ffffff',                 // main content plane
    elevated:     '#ffffff',                 // raised card with shadow
    intelligence: 'linear-gradient(180deg, #f5f2ff 0%, #ffffff 100%)',  // ARIA insight surfaces
    intelligenceDark: '#0f0d24',             // dark-mode ARIA surface
    command:      '#0a0e1a',                 // primary action rail (Command Center dark bar)
    commandInk:   '#f8fafc',                 // text on command surface
    modal:        '#ffffff',
    modalScrim:   'rgba(9, 12, 24, 0.68)',
    critical:     '#fff5f5',                 // critical alert surface
    criticalInk:  '#7f1d1d',
  },

  // ── ARIA STATE VISUAL LANGUAGE ──────────────────────────────────
  // Each ARIA state has a hue + motion character. Consumers use
  // getAriaVisual(state) to render a consistent visualizer.
  aria: {
    idle:      { hue: '#C9A227', pulse: 'slow',    intensity: 0.15, label: 'ready' },
    listening: { hue: '#e8c65a', pulse: 'wave',    intensity: 0.65, label: 'listening' },
    thinking:  { hue: '#C9A227', pulse: 'orbit',   intensity: 0.55, label: 'thinking' },
    analyzing: { hue: '#e8c65a', pulse: 'scan',    intensity: 0.75, label: 'analyzing' },
    found:     { hue: '#10b981', pulse: 'focus',   intensity: 1.00, label: 'found something' },
    warning:   { hue: '#d97706', pulse: 'attend',  intensity: 0.80, label: 'attention' },
    critical:  { hue: '#dc2626', pulse: 'urgent',  intensity: 1.00, label: 'critical' },
    complete:  { hue: '#059669', pulse: 'confirm', intensity: 0.90, label: 'complete' },
    speaking:  { hue: '#8b5cf6', pulse: 'voice',   intensity: 0.85, label: 'speaking' },
  },

  // ── MOTION LANGUAGE ─────────────────────────────────────────────
  // Consistent easing + duration tokens. Consumers apply via CSS
  // transition: `${duration.standard}ms ${easing.standard}`.
  //
  // Respect prefers-reduced-motion: helpers should short-circuit to
  // duration=0 when window.matchMedia('(prefers-reduced-motion: reduce)')
  // reports true.
  motion: {
    duration: {
      instant:      80,    // pointer-down feedback, checkbox toggle
      quick:        160,   // hover, focus ring
      standard:     240,   // panel reveal, tab change
      intelligence: 360,   // ARIA state transitions, insight card reveal
      workspace:    480,   // large view swap, drawer open
    },
    easing: {
      standard:   'cubic-bezier(0.4, 0.0, 0.2, 1)',            // Material standard
      decelerate: 'cubic-bezier(0.0, 0.0, 0.2, 1)',            // enter
      accelerate: 'cubic-bezier(0.4, 0.0, 1.0, 1)',            // exit
      emphasized: 'cubic-bezier(0.2, 0.0, 0.0, 1.0)',          // ARIA reveal
      spring:     'cubic-bezier(0.34, 1.56, 0.64, 1)',         // slight overshoot (positive confirmations only)
    },
  },

  // ── TEXT HIERARCHY ──────────────────────────────────────────────
  // The vision says: important numbers should be large; labels calm;
  // metadata recede. Each entry is size / weight / line-height /
  // letter-spacing / usage.
  text: {
    display:     { size: 48, weight: 700, lh: 1.05, tracking: '-0.03em', use: '$284,817 style hero numbers' },
    displaySm:   { size: 36, weight: 700, lh: 1.10, tracking: '-0.025em', use: 'KPI tile hero numbers' },
    titleXl:     { size: 28, weight: 700, lh: 1.15, tracking: '-0.02em', use: 'Command Center section titles' },
    title:       { size: 22, weight: 700, lh: 1.2,  tracking: '-0.015em', use: 'Page titles' },
    titleSm:     { size: 17, weight: 650, lh: 1.25, tracking: '-0.01em', use: 'Card titles, subsection headers' },
    body:        { size: 14, weight: 500, lh: 1.5,  tracking: '0',        use: 'Primary body text' },
    bodySm:      { size: 13, weight: 500, lh: 1.5,  tracking: '0',        use: 'Secondary body, list items' },
    caption:     { size: 12, weight: 500, lh: 1.4,  tracking: '0',        use: 'Timestamps, metadata' },
    eyebrow:     { size: 10, weight: 700, lh: 1.3,  tracking: '0.14em',   use: 'SECTION LABELS uppercase' },
    mono:        { size: 12, weight: 500, lh: 1.4,  tracking: '0',        use: 'IDs, SIDs, numeric refs (tabular)' },
    ariaSpeech:  { size: 15, weight: 500, lh: 1.55, tracking: '0',        use: 'ARIA narration copy' },
    ariaLabel:   { size: 10, weight: 700, lh: 1.2,  tracking: '0.2em',    use: 'ARIA STATE labels uppercase' },
  },

  // ── SIGNAL SEPARATION ───────────────────────────────────────────
  // Semantic (success/warning/critical/positive/negative) is DIFFERENT
  // from accent (AI purple / Health teal / Revenue gold). Signal
  // conveys state; accent conveys category. Never mix.
  signal: {
    positive:      { fg: '#059669', bg: '#e7f8ef', border: '#a8ddc0' },
    negative:      { fg: '#dc2626', bg: '#ffebec', border: '#f4b4b6' },
    warning:       { fg: '#d97706', bg: '#fff4dc', border: '#f4d99a' },
    critical:      { fg: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
    neutral:       { fg: '#4b5563', bg: '#f1f5f9', border: '#e2e8f0' },
    informational: { fg: '#1e3a8a', bg: '#eff6ff', border: '#bfdbfe' },
  },
  accent: {
    ai:      { fg: '#6754f5', bg: '#efedff', border: '#d9d3ff', hover: '#5945e8' },
    health:  { fg: '#0f9f88', bg: '#e4f8f3', border: '#b8eadf', hover: '#0b8874' },
    life:    { fg: '#0d5db6', bg: '#e6f0fb', border: '#bfd8f0', hover: '#0a4d97' },  // life-insurance category
    revenue: { fg: '#d99016', bg: '#fff4d9', border: '#f4d99a', hover: '#b8790e' },
  },

  // ── GLOW POLICY ─────────────────────────────────────────────────
  // Glow is rare per master directive. Only 5 legitimate uses:
  //   1. ARIA active/listening/found state
  //   2. Primary action (Start My Day, Call Now)
  //   3. High-value intelligence card (single-most-important insight)
  //   4. System processing (ARIA scanning something big)
  //   5. Critical anomaly (fraud/compliance alert)
  // Never on hovers, never on decoration.
  glow: {
    ariaActive: '0 0 0 4px rgba(103,84,245,0.18), 0 8px 32px rgba(103,84,245,0.24)',
    primary:    '0 4px 16px rgba(103,84,245,0.32)',
    intelligence: '0 0 0 1px rgba(168,85,247,0.28), 0 12px 48px rgba(168,85,247,0.16)',
    processing: '0 0 0 2px rgba(139,92,246,0.14)',
    critical:   '0 0 0 3px rgba(220,38,38,0.22), 0 8px 32px rgba(220,38,38,0.18)',
  },

  // ── RADIUS RULES PER SURFACE TYPE ──────────────────────────────
  // Master directive: don't turn everything into giant pills.
  radius: {
    button:     8,     // buttons, chips
    input:      8,     // form fields
    card:       12,    // panels, insight cards
    surface:    16,    // large workspace panels
    modal:      18,    // modal dialogs
    intelligence: 20,  // ARIA insight cards (slightly softer)
    pill:       999,   // status chips, tags (small only)
  },

  // ── SPACING SCALE ───────────────────────────────────────────────
  // Vision: increase breathing room. Important information should not
  // be cramped. This scale is roomier than the legacy `spacing` above.
  space: {
    xxs: 4,
    xs:  8,
    sm:  12,
    md:  16,
    lg:  24,
    xl:  32,
    xxl: 48,
    xxxl: 64,
  },

  // ── LAYOUT ──────────────────────────────────────────────────────
  layout: {
    sidebarWidth:   232,      // matches existing LeftSidebar
    ariaRailWidth:  360,      // right-side ARIA persistence rail (Phase 3)
    topBarHeight:   68,       // matches existing GlobalTopBar
    contentMax:     1560,     // slightly narrower than cockpit.maxContent for readability
    contentGutter:  32,
  },

  // ── TYPOGRAPHY FAMILIES ─────────────────────────────────────────
  // System-first stacks. No @font-face required — avoids the FOUT +
  // network cost of loading a custom face. Meets modern-web-design
  // performance-first principle. Cockpit tokens above use "Inter" as
  // a preference; this block declares the same intention for the
  // sixfigure surfaces without demanding Inter be present.
  type: {
    display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    body:    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono:    '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  },

  // ── COMMAND BAR (Phase 3) ───────────────────────────────────────
  commandBar: {
    // Slash-command style; opens on Cmd+K / Ctrl+K
    background:   '#0a0e1a',
    borderTop:    'rgba(255,255,255,0.06)',
    inputBg:      'rgba(255,255,255,0.06)',
    inputFocusBg: 'rgba(255,255,255,0.10)',
    placeholder:  '#94a3b8',
    resultHover:  'rgba(255,255,255,0.06)',
    shortcutBg:   'rgba(255,255,255,0.09)',
  },
};

// Helper: returns motion duration with prefers-reduced-motion respected.
// Consumers use like `transition: `${motionDuration('standard')}ms ...`
export function motionDuration(name) {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    return 0;
  }
  return sixfigure.motion.duration[name] ?? sixfigure.motion.duration.standard;
}

// Helper: assemble a CSS text style from a token entry.
// Usage: <div style={textStyle('display')}>$284,817</div>
export function textStyle(key) {
  const t = sixfigure.text[key];
  if (!t) return {};
  return {
    fontSize: t.size,
    fontWeight: t.weight,
    lineHeight: t.lh,
    letterSpacing: t.tracking,
  };
}
